import React, {
  Fragment, ReactElement, useEffect, useState,
} from 'react'
import { FiSearch } from 'react-icons/fi'
import Button, { ButtonStyle } from '../buttons/Button'
import Loading from '../components/Loading'
import InputField from '../fields/InputField'
import SelectField, { ISelectOption } from '../fields/SelectField'
import { useNotFirstRender } from '../helpers/firstRender'
import useIsMobile from '../helpers/isMobile'
import { useDebounceFunction } from '../helpers/useDebounce'
import useSession, { clearAllSessions } from '../helpers/useSessionState'
import colors from '../lib/colors'
import Pagination from './Pagination'
import TableBody from './TableBody'
import TableDates from './TableDates'
import TableFilters from './TableFilters'
import TableHead from './TableHead'
import {
  extractFilterValues,
  getFetch,
  setUpOptions,
  initString,
  setUpDates,
  extractSlug,
} from './tableHelper'
import {
  ButtonRow,
  Controls,
  Cover,
  FilterRow,
  LoadingLimiter,
  StyledTable,
} from './TableStyles'
import { ITableData, ITableProps } from './tableTypes'

enum TableSession {
  Data = 'tableData',
  Options = 'tableOptions',
  Search = 'tableSearch',
  Filters = 'tableFilterValues',
  Dates = 'tableDates',
  CustomFilter = 'tableCustom',
}

export default function Table<T extends {}>(
  props: ITableProps<T>,
): ReactElement {
  const {
    columns,
    fetch,
    rowLink,
    options: defaultOptions,
    search: defaultSearch,
    restrictions,
    dates: defaultDates,
    filters,
    getState,
    getData,
    getFiltered,
    defaults,
    forgetful,
    reset,
    pagination = true,
    controls,
    customFilter,
  } = props

  const {
    Data, Options, Search, Filters, Dates, CustomFilter,
  } = TableSession

  const [data, setData] = useSession<ITableData<T>>(Data, undefined, forgetful)

  const initOptions = setUpOptions(defaultOptions)
  const initSearch = initString(defaultSearch)
  const initDates = setUpDates(defaultDates)
  const initFilters = extractFilterValues(filters)
  const initSlug = extractSlug(filters)

  const [options, setOptions, updateOptions] = useSession(
    Options,
    initOptions,
    forgetful,
  )
  const [search, setSearch] = useSession(Search, initSearch, forgetful)
  const [dates, setDates, updateDates] = useSession(Dates, initDates, forgetful)
  const [filterValues, setFilterValues] = useSession(
    Filters,
    initFilters,
    forgetful,
  )

  const [slug, setSlug] = useState(initSlug)

  type Custom = ISelectOption<unknown> | undefined
  const [customFilterValue, setCustomFilterValue] = useSession<Custom>(
    CustomFilter,
    undefined,
    forgetful,
  )

  const [loading, setLoading] = useState(false)
  const notFirstRender = useNotFirstRender()

  const handleFetch = async (): Promise<void> => {
    const visibleReload = !data || notFirstRender || forgetful
    if (visibleReload) setLoading(true)

    const custom = customFilter && customFilterValue
      ? { accessor: customFilter.accessor, value: customFilterValue.value }
      : undefined

    const params = getFetch(
      options,
      filterValues,
      search,
      dates,
      defaults,
      slug,
      custom,
    )
    if (!params) return

    const res = await fetch(params)

    if (res) {
      setData(res)
      if (getData && res.data) getData(res.data)
      if (getFiltered) {
        const { recordsFiltered, recordsTotal } = res || {}
        getFiltered({ recordsFiltered, recordsTotal })
      }
    }
    if (getState) getState(params)

    setLoading(false)
  }

  const debounceHandleFetch = useDebounceFunction(handleFetch, 500)

  useEffect(() => {
    debounceHandleFetch()
  }, [options, filterValues, dates, customFilterValue])

  const firstPage = (): void => updateOptions('skip', 0)
  const clearAll = (): void => {
    clearAllSessions(TableSession)
    setOptions(initOptions)
    setSearch(initSearch)
    setDates(initDates)
    setFilterValues(initFilters || [])
    setCustomFilterValue(undefined)
    firstPage()
  }

  useEffect(() => {
    if (notFirstRender) clearAll()
  }, [defaults, reset])

  const {
    skip, limit, orderDirection, orderBy,
  } = options
  const { data: rows, recordsTotal, recordsFiltered } = data || {}

  const hasFilters = defaultSearch || filters || defaultDates

  return (
    <Fragment>
      <Controls>
        {hasFilters && (
          <FilterRow>
            {defaultSearch && (
              <InputField
                placeholder="Search..."
                onChange={(value): void => {
                  const allowed = !(restrictions && !value.match(restrictions))
                  if (allowed) {
                    setSearch(value)
                    firstPage()
                  }
                }}
                icon={<FiSearch />}
                value={search}
              />
            )}
            {filters && (
              <TableFilters
                filters={filters}
                values={filterValues}
                setValues={setFilterValues}
                resetPage={firstPage}
                setSlug={setSlug}
              />
            )}
            {customFilter && (
              <SelectField
                options={customFilter.options}
                onChange={(option): void => setCustomFilterValue(option || undefined)}
                value={customFilterValue}
                placeholder={customFilter.placeholder}
                isClearable={true}
              />
            )}
            {defaultDates && (
              <TableDates
                dates={dates}
                updateDates={updateDates}
                resetPage={firstPage}
              />
            )}
            <Button onClick={clearAll} buttonStyle={ButtonStyle.Secondary}>
              Reset
            </Button>
          </FilterRow>
        )}
        {controls && <ButtonRow>{controls}</ButtonRow>}
      </Controls>
      <div style={{ position: 'relative' }}>
        {loading && (
          <Cover>
            <LoadingLimiter>
              <Loading spinnerColor={colors.blue} />
            </LoadingLimiter>
          </Cover>
        )}
        <StyledTable theme={{ isMobile: useIsMobile() }}>
          <TableHead
            columns={columns}
            updateOptions={updateOptions}
            orderDirection={orderDirection}
            orderBy={orderBy}
          />
          <TableBody
            rows={rows}
            columns={columns}
            loading={loading}
            rowLink={rowLink}
          />
        </StyledTable>
      </div>
      {pagination && (
        <Pagination
          skip={skip}
          limit={limit}
          actualRecords={rows?.length}
          recordsTotal={recordsTotal}
          recordsFiltered={recordsFiltered}
          update={updateOptions}
          loading={loading}
        />
      )}
    </Fragment>
  )
}
